import { DownloadManagerState, DownloadStatus } from '../containers/DownloadManager/reducer';
import DownloadManagerUtils from './downloadManager';
import DriveItemUtils from './driveItem';
import { Api } from '../typings/api';

export enum OfflineDownloadDebugAction {
	PhaseStart = 'PHASE_START',
	PhaseEnd = 'PHASE_END',
	Skip = 'SKIP',
	DepWalk = 'DEP_WALK',
	AlreadyComplete = 'ALREADY_COMPLETE',
	DiskCheck = 'DISK_CHECK',
	DiskConfirm = 'DISK_CONFIRM',
	DiskRedownload = 'DISK_REDOWNLOAD',
	NativeZip = 'NATIVE_ZIP',
	BackfillQueue = 'BACKFILL_QUEUE',
	DepProgress = 'DEP_PROGRESS',
	Timing = 'TIMING',
}

export enum OfflineDownloadPhase {
	RequestMobileAppDownload = 'requestMobileAppDownload',
	DownloadItemWithDependencies = 'downloadItemWithDependencies',
	NativeDownload = 'nativeDownload',
	NativeDownloadPostUnzip = 'nativeDownloadPostUnzip',
	DownloadRecursively = 'downloadRecursively',
	GetInitialDependenciesUpdates = 'getInitialDependenciesUpdates',
	UpdateDriveItemWithDependencies = 'updateDriveItemWithDependencies',
	AfterOutcomingsRefresh = 'afterOutcomingsRefresh',
	BackfillIncompleteDependencies = 'backfillIncompleteDependencies',
	RepairUnconfirmedDownloads = 'repairUnconfirmedDownloads',
	ConfirmDownloadsOnDisk = 'confirmDownloadsOnDisk',
	DownloadImportantAssets = 'downloadImportantAssets',
	DownloadImportantAssetsDecision = 'downloadImportantAssetsDecision',
	ViewPublicationState = 'viewPublicationState',
	PublicationUseLocalUrl = 'publicationUseLocalUrl',
	OpenFrameInvoke = 'openFrameInvoke',
	ExistFile = 'existFile',
	ExistDirectory = 'existDirectory',
}

export enum OfflineDownloadTimedPhase {
	ViewPublication = 'viewPublication',
	OpenFrameNative = 'openFrameNative',
	InitEndInitialization = 'initEndInitialization',
	InitGetInitialDependenciesUpdates = 'initGetInitialDependenciesUpdates',
	InitRemoveItemsMarkedForDeletion = 'initRemoveItemsMarkedForDeletion',
	InitDownloadRequiredItems = 'initDownloadRequiredItems',
	InitDownloadAllBgMedia = 'initDownloadAllBgMedia',
	InitDownloadBrandAndFolderIcons = 'initDownloadBrandAndFolderIcons',
	InitDownloadThumbs = 'initDownloadThumbs',
	InitDeleteObsoleteItems = 'initDeleteObsoleteItems',
	InitDeleteObsoleteThumbsAndBgMedia = 'initDeleteObsoleteThumbsAndBgMedia',
}

export enum OfflineDownloadReason {
	AlreadyProcessedInBatch = 'alreadyProcessedInBatch',
	LogicallyCompleteNoZip = 'logicallyCompleteNoZip',
	NothingToBackfill = 'nothingToBackfill',
	NotLogicallyDownloaded = 'notLogicallyDownloaded',
	MissingOnDisk = 'missingOnDisk',
	ImportantOfflineShortcut = 'importantOfflineShortcut',
	ItemMarkedForDeletion = 'itemMarkedForDeletion',
	RequestMobileAppDownloadUpdatable = 'requestMobileAppDownloadUpdatable',
	NeedsDownloadFalse = 'needsDownloadFalse',
	NativeZipInProgress = 'nativeZipInProgress',
	Offline = 'offline',
}

export enum OfflineDownloadDecision {
	Download = 'download',
	DownloadMissingMetaOrManager = 'downloadMissingMetaOrManager',
	SkipShortcut = 'skipShortcut',
	SkipLogicallyComplete = 'skipLogicallyComplete',
	DownloadVersionUpdate = 'downloadVersionUpdate',
}

export enum OfflineDownloadBranch {
	ErrorRetry = 'errorRetry',
	Init = 'init',
	ResumeOrMissingStatus = 'resumeOrMissingStatus',
	VersionUpdate = 'versionUpdate',
}

type OfflineLogPhase = OfflineDownloadPhase | OfflineDownloadTimedPhase;

/**
 * Temporary offline download tracing.
 * Disable: set OFFLINE_DOWNLOAD_DEBUG_DEFAULT to false, or localStorage.offlineDownloadDebug = '0'
 * Enable:  localStorage.offlineDownloadDebug = '1'
 */
const OFFLINE_DOWNLOAD_DEBUG_DEFAULT = false;
const OFFLINE_DOWNLOAD_DEBUG_STORAGE_KEY = 'offlineDownloadDebug';
const OFFLINE_DOWNLOAD_DEBUG_OFF = '0';
const OFFLINE_DOWNLOAD_DEBUG_ON = '1';

export const isOfflineDownloadDebugEnabled = (): boolean => {
	try {
		if (typeof window !== 'undefined' && window.localStorage) {
			const stored = window.localStorage.getItem(OFFLINE_DOWNLOAD_DEBUG_STORAGE_KEY);
			if (stored === OFFLINE_DOWNLOAD_DEBUG_OFF) {
				return false;
			}
			if (stored === OFFLINE_DOWNLOAD_DEBUG_ON) {
				return true;
			}
		}
	} catch (error) {
		// ignore storage errors in WebView
	}
	return OFFLINE_DOWNLOAD_DEBUG_DEFAULT;
};

const formatDetails = (details?: Record<string, string | number | boolean | undefined>): string => {
	if (!details) {
		return '';
	}

	return Object.keys(details)
		.filter(key => details[key] !== undefined)
		.map(key => `${key}=${details[key]}`)
		.join(' ');
};

export const logOfflineDownload = (
	action: OfflineDownloadDebugAction,
	phase: OfflineLogPhase,
	itemId?: number,
	details?: Record<string, string | number | boolean | undefined>,
): void => {
	if (!isOfflineDownloadDebugEnabled()) {
		return;
	}

	const idPart = itemId !== undefined ? ` itemId=${itemId}` : '';
	const detailsPart = formatDetails(details);
	const suffix = detailsPart ? ` ${detailsPart}` : '';

	// tslint:disable-next-line:no-console intentional opt-in debug tracing, off by default
	console.log(`[OfflineDownload] ${action} phase=${phase}${idPart}${suffix}`);
};

export const startTimedPhase = (phase: OfflineDownloadTimedPhase, itemId?: number): number => {
	if (isOfflineDownloadDebugEnabled()) {
		logOfflineDownload(OfflineDownloadDebugAction.Timing, phase, itemId, {
			event: 'start',
			at: Date.now(),
		});
	}
	return Date.now();
};

export const endTimedPhase = (
	phase: OfflineDownloadTimedPhase,
	startMs: number,
	itemId?: number,
	details?: Record<string, string | number | boolean | undefined>,
): void => {
	if (!isOfflineDownloadDebugEnabled()) {
		return;
	}

	logOfflineDownload(OfflineDownloadDebugAction.Timing, phase, itemId, {
		event: 'end',
		elapsedMs: Date.now() - startMs,
		at: Date.now(),
		...details,
	});
};

export const runTimedInitPhase = <T>(
	phase: OfflineDownloadTimedPhase,
	fn: () => T | PromiseLike<T>,
	itemId?: number,
): Promise<T> => {
	const startMs = startTimedPhase(phase, itemId);
	return Promise.resolve(fn())
		.then((result) => {
			endTimedPhase(phase, startMs, itemId);
			return result;
		})
		.catch((error) => {
			endTimedPhase(phase, startMs, itemId, { error: true });
			throw error;
		});
};

export const getUpdatableDependencyDetails = (
	driveItem: Api.DriveItem,
	downloadManager: DownloadManagerState,
): string => {
	const itemFromDownloadManager = downloadManager.items[driveItem.id];

	if (!itemFromDownloadManager || !driveItem.dependencies || driveItem.dependencies.length === 0) {
		return 'none';
	}

	const outdated = driveItem.dependencies
		.filter((dependency) => {
			const downloadedDependency = downloadManager.items[dependency.id];
			return !downloadedDependency || dependency.version > downloadedDependency.version;
		})
		.map((dependency) => {
			const downloadedDependency = downloadManager.items[dependency.id];
			const storedVersion = downloadedDependency ? downloadedDependency.version : 'missing';
			return `${dependency.id}(api=${dependency.version},stored=${storedVersion})`;
		});

	return outdated.length > 0 ? outdated.join(';') : 'none';
};

export const getDependencyProgressSnapshot = (
	downloadManager: DownloadManagerState,
	parentId: number,
): { downloaded: number; total: number } | undefined => {
	const parentItem = downloadManager.items[parentId];

	if (!parentItem || parentItem.dependencies.length === 0) {
		return undefined;
	}

	const downloaded = parentItem.dependencies.reduce((total, dependencyId) => {
		const dependencyItem = downloadManager.items[dependencyId];
		if (dependencyItem && dependencyItem.status === DownloadStatus.NATIVE_DOWNLOADED) {
			return total + 1;
		}
		return total;
	}, 0);

	return {
		downloaded,
		total: parentItem.dependencies.length,
	};
};

export const getBackfillReason = (
	downloadManager: DownloadManagerState,
	driveItem: Api.DriveItem,
): string => {
	const reasons: string[] = [];

	if (DownloadManagerUtils.hasIncompleteDependencies(downloadManager, driveItem.id)) {
		reasons.push('incompleteDependencies');
	}

	if (DownloadManagerUtils.hasStaleNativeZipStatus(downloadManager, driveItem.id)) {
		reasons.push('staleNativeZip');
	}

	if (DriveItemUtils.hasUpdatableDependencies(driveItem, downloadManager)) {
		reasons.push(`updatableDependencies[${getUpdatableDependencyDetails(driveItem, downloadManager)}]`);
	}

	return reasons.length > 0 ? reasons.join('+') : 'unknown';
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/offlineDownloadDebug.ts